import React, { Suspense } from 'react';
import { Box } from '@mui/material';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import Layout from '../layout/Layout';
import { BootHost } from '../common/EnsoBootLoader';
import { useBootHold } from '../common/bootHold';
import RouteLoadingFallback from '../common/RouteLoadingFallback';
import { ErrorBoundary } from '../ErrorBoundary';

/**
 * Auth gate + app chrome as a layout route (single Routes tree, Outlet).
 * Avoids nested <Routes> which breaks router context for lazy pages.
 */
const RequireAuth: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  useBootHold(loading);

  // Fragment + first-child BootHost in both branches keeps the same instance
  // mounted, so the splash can fade out over the already-rendered app.
  if (loading) {
    return (
      <>
        <BootHost />
      </>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // The boot splash is only for the initial session check above; route and
  // data loading inside the shell use in-content skeletons.
  return (
    <>
      <BootHost />
      <Box sx={{ height: '100%', width: '100%' }}>
        <Layout>
          {/* Page-level boundary inside the shell: a crashing page keeps the
              sidebar, and navigating away clears the error. */}
          <ErrorBoundary region="page" resetKey={location.pathname}>
            {/* Keyed on the top-level segment: navigations run in a transition
                (v7_startTransition), which holds the old page on an already
                shown boundary until the next page's chunk loads. A fresh
                boundary shows the skeleton at once. /projects/... shares one key
                so the workspace is not remounted between its tabs. */}
            <Suspense key={location.pathname.split('/')[1] ?? ''} fallback={<RouteLoadingFallback />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </Layout>
      </Box>
    </>
  );
};

export default RequireAuth;
